import React, { useEffect, useRef, useState } from "react";
import { Space, Table, Tag, Button, Input, message, Drawer, Col, Row, Form, Tabs, ConfigProvider, Modal, Upload, UploadFile, Spin } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { getLabel, formatDateTime, isNotNullUndefined, isNotNullUndefinedBlank } from "../helpers/common";
import Pagination from "../helpers/pagination";
import { useFetchCommissionsList, useUpdateCommissions } from "../services/refercommisionsummary.service";
import { azureBlobStorageConfig } from "../helpers/config";
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { eventEmitter } from "../helpers/eventemitter";
import { BlobServiceClient } from '@azure/storage-blob';
import { useGetSasToken } from '../services/blobcontext.service';
import IResponse from "../types/response";
import { useAuth } from "../context/AuthContext";
const ReferCommissionsSummary: React.FC = () => {

    const { Search } = Input;
    const { Column } = Table;
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setIsLoading] = useState(false);
    const [commissionsList, setCommissionsList] = useState<any>([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [featuredImage, setFeaturedImage] = useState<UploadFile[]>([]);
    const [open, setOpen] = useState(false);
    const filters = useRef({ statusFilter: 'All' });
    const { TabPane } = Tabs;
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [modalType, setModalType] = useState<'delete' | 'add' | 'update' | ''>('');
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [formUpdateValues, setFormUpdateValues] = useState<any>({});
    const searchValue = useRef<string>("");
    const [previewImage, setPreviewImage] = useState('');
    const [sasToken, setSasToken] = useState<string | null>(null);
    const [commissionStatuses, setCommissionStatuses] = useState<any[]>([
        { id: 0, name: "All" },
        { id: 1, name: "Active" },
        { id: 2, name: "Started Earning" },
        { id: 3, name: "Ready for Withdrawal" },
        { id: 4, name: "Requested for Withdrawal" }
    ]);
    const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

    useEffect(() => {
            loadPermissions();
    }, [permissionsLoaded]);
    const CommissionEdit = hasPermission("COMMISSION", "UPDATE");
    //#region on-load useEffect operations
    useEffect(() => {
        let userRoleId = parseInt(storage.getUserRoleId() ?? "0");
        if (userRoleId !== 1) {
            navigate("/");
        }
        else {
            setIsLoading(true);
            fetchCommissionsList("", "", "");
            getSasToken();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize, filters.current]);


    const getSasToken = () => {
        getSasTokenMutation.mutate({});
    }

    const getSasTokenMutation = useGetSasToken({
        onSuccess: (response: IResponse) => {
            if (response?.data != null) {
                setSasToken(response.data);
                message.success(response.statusMessage);
                return;
            }
            message.error(response.statusMessage);
        },
        onError: (error) => {
            message.error(error.message);
        }
    });

    const onSearch = (value: any) => {
        fetchCommissionsList("", value, "");
        searchValue.current = value;
    };

    const tabClicked = (key: number) => {
        const status = commissionStatuses.find((x: any) => x.id === key);
        let statusName = status.name;
        if (statusName == "All") {
            statusName = "";
        }
        fetchCommissionsList("", "", statusName);
    };

    const showDrawer = (record: any) => {
        form.setFieldsValue({
            trackingID: record.trackingID,
        });
        setIsLoading(false);
        setOpen(true);
    }

    const fetchCommissionsList = async (userID: string, search: string, status: string) => {
        setIsLoading(true);
        setCommissionsList([]);
        fetchCommissionsListMutation.mutate({ userID: userID, searchFilter: search, statusName: status });
    };

    const fetchCommissionsListMutation = useFetchCommissionsList({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {

                if (isNotNullUndefined(response.data) && response.data.length > 0) {
                    const formattedData = response.data.map((data: any) => ({
                        ...data,
                        totalCommission: `$${(data.totalCommission ?? 0).toFixed(2)}`,
                        createdAt: formatDateTime(data.createdAt, "DD/MM/YYYY HH:mm AMPM"),
                        updatedAt: formatDateTime(data.updatedAt, "DD/MM/YYYY HH:mm AMPM"),
                        statusName: [data.statusName]
                    }));
                    setCommissionsList(formattedData);
                    const recordCount = formattedData[0].recordCount;
                    setTotalPages(Math.ceil(recordCount / pageSize));
                    setIsLoading(false);
                } else {
                    setCommissionsList([]);
                    setIsLoading(false);
                }
            } else {
                message.error(response.statusMessage);
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
            setIsLoading(false);
        }
    });

    const onClose = () => {
        setOpen(false);
        eventEmitter.emit("clickableChange", false);
        form.resetFields();
    };

    const getModalTitle = () => {
        let message = "";
        if (modalType === 'delete') {
            message = "Confirm Deactivation!";
        } else if (modalType === 'add') {
            message = "Confirm Update!";
        }
        return message;
    }

    const getModelContent = () => {
        let modalContent = <></>;
        if (modalType === "add") {
            modalContent = (
                <p>Are you sure you want to <b>Update</b> this user's commission request?<b>Do you wish to proceed?</b></p>
            );
        }
        return modalContent;
    }

    const updateCommission = useUpdateCommissions({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                fetchCommissionsList("", "", "");
                let succesMessage = "Commission has been updated successfully!";
                setIsLoading(false);
                message.success(succesMessage);
            } else {
                setIsLoading(false);
                message.error(response.statusMessage);
            }
            onClose();
            form.resetFields();
        },
        onError: (error: any) => {
            setIsLoading(false);
            message.error(error.statusMessage);
        }
    });

    const handleSave = () => {
        try {
            if (modalType === 'add') {
                setIsLoading(true);
                updateCommission.mutate({ trackingID: formUpdateValues.trackingID, comment: formUpdateValues.comment, paymentProofImage: formUpdateValues.paymentProofImage.file.url });
            }
            setIsConfirmModalVisible(false);
        } catch (error) {
            message.error('Failed to save');
        }
    };

    const handleCancel = () => {
        setIsLoading(false);
        setIsConfirmModalVisible(false);
    };

    const onFinish = (values: any) => {
        setModalType('add');
        setIsConfirmModalVisible(true);
        setFormUpdateValues(values);
    }

    function getBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handlePreview = async (file: UploadFile) => {
        debugger
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as File);
        }
        setPreviewImage(file.url || (file.preview as string));
        setPreviewVisible(true);
    };

    const handlePreviewCancel = () => setPreviewVisible(false);

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div className="ant-upload-text">Upload</div>
        </div>
    );

    //#region pagination operation
    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        const size = pageSize;
        setPageSize(size);
    };

    const handleImageChange = async ({ fileList }: { fileList: UploadFile[]; }) => {
        if (fileList.length > 0) {
            // Loop through each file in the list.
            for (const x of fileList) {
                if (x.status === 'uploading' && x.originFileObj && !x.url) {
                    // Generate a unique file name.
                    const fileExtension = x.originFileObj.name.split(".").pop();
                    const fileName = "payment-proof_" + new Date().getTime() + "." + fileExtension;
                    try {
                        // Initialize the BlobServiceClient using account name and SAS token.
                        const blobServiceClient = new BlobServiceClient(
                            `https://${azureBlobStorageConfig.accountName}.blob.core.windows.net/?${sasToken}`
                        );
                        // Get the container client.
                        const containerClient = blobServiceClient.getContainerClient(azureBlobStorageConfig.containerName as string);
                        // Define the blob path (using a "temp/" folder).
                        const blobPath = "temp/" + fileName;
                        const blockBlobClient = containerClient.getBlockBlobClient(blobPath);
                        // Upload the file using the browser data API.
                        await blockBlobClient.uploadBrowserData(x.originFileObj, {
                            blobHTTPHeaders: { blobContentType: x.originFileObj.type }
                        });
                        // Get the URL of the uploaded blob.
                        const fileUrl = blockBlobClient.url;
                        x.url = fileUrl;
                        const featuredImageFile: UploadFile = {
                            uid: '1',
                            name: 'Payment Proof',
                            status: 'done',
                            url: fileUrl,
                        };
                        setFeaturedImage([featuredImageFile]);
                    } catch (error: any) {
                        message.error(error.message || 'Something went wrong. Please try again!');
                        return false;
                    }
                }
            }
        }
    };

    const onDeleteFeatureImageFile = async (file: UploadFile<any>) => {
        if (file && isNotNullUndefinedBlank(file.url)) {
            const fileName = file.url?.split('/').pop();
            if (isNotNullUndefinedBlank(fileName)) {
                try {
                    // Initialize the BlobServiceClient.
                    const blobServiceClient = new BlobServiceClient(
                        `https://${azureBlobStorageConfig.accountName}.blob.core.windows.net/?${sasToken}`
                    );
                    // Get the container client.
                    const containerClient = blobServiceClient.getContainerClient(azureBlobStorageConfig.containerName as string);
                    // Determine the blob path based on the file URL.
                    let blobPath = "";
                    if (file.url?.includes("/temp/")) {
                        blobPath = "temp/" + fileName;
                    } else {
                        // For single file uploads, if not in "temp", assume it is in "PaymentProof/".
                        blobPath = "PaymentProof/" + fileName;
                    }
                    const blockBlobClient = containerClient.getBlockBlobClient(blobPath);
                    await blockBlobClient.deleteIfExists();
                } catch (error: any) {
                    message.error("Error deleting file: " + error.message);
                    return false;
                }
                const images = featuredImage.filter(x => x.url !== file.url);
                setFeaturedImage(images);
            }
        }
    };

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-2xl font-semibold text-black">Refer Commission Summary</h2>
                <div className="w-80 max-w-80">
                    <Search
                        placeholder="Search Commission Summary"
                        size="large"
                        enterButton
                        className="search-field"
                        onSearch={onSearch}
                    />
                </div>
            </div>

            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                        },
                    }
                }}
            >
                <Tabs defaultActiveKey="0" onTabClick={(e) => { tabClicked(parseInt(e)) }}>
                    {
                        commissionStatuses.map((x: any) => (
                            <TabPane tab={`${x.name}`} key={`${x.id}`}>
                                <Table dataSource={commissionsList} className="commissionlist-tabs-table" bordered pagination={{ pageSize: pageSize, position: ['none'] }} locale={{
                                    emptyText: loading ? <Spin /> : (
                                        <div className="ant-empty">
                                            <div className="ant-empty-image">
                                                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                                    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                                        <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                                        <g fillRule="nonzero" stroke="#d9d9d9">
                                                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                            <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                                        </g>
                                                    </g>
                                                </svg>
                                            </div>
                                            <div className="ant-empty-description">No data</div>
                                        </div>
                                    )
                                }}
                                >
                                    <Column title="User Name" dataIndex="userName" key="userName" align="center" />
                                    <Column title="Email" dataIndex="userEmail" key="userEmail" align="center" />
                                    <Column title="Total Commission" dataIndex="totalCommission" key="totalCommission" align="center" />
                                    <Column
                                        align="center"
                                        title="Status"
                                        dataIndex="status"
                                        key="status"
                                        render={(status) => (
                                            <Tag
                                                key={status}
                                                color={
                                                    status === "Started Earning" ? "blue" : status === "Active" ? "green" : status === "Ready for Withdrawal" ? "orange" : status === "Requested for Withdrawal" ? "yellow" : "green"
                                                }
                                                className="m-0 pl-1 pr-1"
                                            >
                                                {status.toUpperCase()}
                                            </Tag>
                                        )}
                                    />
                                    <Column title="Created At" dataIndex="createdAt" key="createdAt" align="center" />
                                    <Column title="Updated At" dataIndex="updatedAt" key="updatedAt" align="center" />
                                    <Column
                                        align="center"
                                        title="Action"
                                        key="action"
                                        render={(_: any, record: any) => (
                                            <Space size="middle">
                                                {(record.statusId == "3" || record.statusId == "4") &&  CommissionEdit && (
                                                    <Button onClick={() => showDrawer(record)} className="h-10 bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out">Attach Proof</Button>
                                                )}
                                            </Space>
                                        )}
                                    />
                                </Table>
                                <div>
                                    <Pagination
                                        currentPage={pageNumber.current}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </TabPane>
                        ))
                    }
                </Tabs>
                <Drawer title={'Update Commission'} width={470} onClose={onClose} open={open} mask={false}>
                    <div>
                        <Form form={form} layout="vertical" onFinish={onFinish} >
                            <Form.Item name="trackingID" hidden><Input /></Form.Item>
                            <Row gutter={16}>
                                <Form.Item
                                    name="trackingID"
                                    label="Place ID"
                                    style={{ display: 'none' }}>
                                    <Input type="hidden" />
                                </Form.Item>
                                <Col span={24}>
                                    <Form.Item name="paymentProofImage" label={getLabel("Upload Payment Proof", true)}>
                                        <Upload
                                            action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                                            listType="picture-card"
                                            fileList={featuredImage}
                                            onPreview={handlePreview}
                                            onChange={handleImageChange}
                                            onRemove={e => onDeleteFeatureImageFile(e)}
                                        >
                                            {featuredImage.length >= 1 ? null : uploadButton}
                                        </Upload>
                                    </Form.Item>
                                    <Form.Item
                                        name="comment"
                                        label="Comment">
                                        <Input.TextArea
                                            className="!h-[150px]"
                                            style={{ resize: 'none', paddingTop: '10px' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Modal open={previewVisible} footer={null} onCancel={handlePreviewCancel}>
                                    <img alt="example" style={{ width: '100%' }} src={previewImage} />
                                </Modal>
                                <div className="flex justify-end w-full gap-5">
                                    <div className="flex gap-2">
                                        <Button
                                            type="default"
                                            onClick={onClose}
                                            className="h-10 text-black border-black hover:bg-black hover:text-white hover:transition hover:duration-500 hover:ease-in-out"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            loading={loading}
                                            type="primary"
                                            htmlType="submit"
                                            onClick={() => form.submit()}
                                            className="h-10 bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out"
                                        >
                                            Update
                                        </Button>
                                    </div>
                                </div>
                            </Row>
                        </Form>
                    </div>
                </Drawer>

                <Modal
                    title={<span className="custom-modal-title">{getModalTitle()}</span>}
                    open={isConfirmModalVisible}
                    onOk={handleSave}
                    onCancel={handleCancel}
                    closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                    footer={
                        <div>
                            <Button key="back" onClick={handleCancel} className="hover:!text-black hover:!border-black">
                                Cancel
                            </Button>
                            <Button key="submit" onClick={handleSave} className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black">
                                Confirm
                            </Button>
                        </div>
                    }
                >
                    {getModelContent()}
                </Modal>
            </ConfigProvider>
        </div>
    );
}

export default ReferCommissionsSummary;